import type { BrandLogo, BrandRecord } from '@/types'
import parkezeMark from '@/assets/parkeze-mark.png'

/** The web prefers the SVG and falls back to the PNG (logbook decision 26). */
const logoSrc = (logo: BrandLogo) => logo.svg ?? logo.png

/**
 * A logo variant with its dark-background partner, picked by the operating
 * system's color scheme as the rest of the page is. A logo with no dark
 * variant never sits on a dark page: the shell keeps such a page light
 * (`light-only`, useBranding.ts; logbook decision 26), so it is never boxed
 * in a white tile either.
 */
function LogoImage({ light, dark, alt, className }: {
  light: BrandLogo
  dark: BrandLogo | null
  alt: string
  className: string
}) {
  return (
    <picture className="contents">
      {dark && <source media="(prefers-color-scheme: dark)" srcSet={logoSrc(dark)} />}
      <img src={logoSrc(light)} alt={alt} className={className} />
    </picture>
  )
}

/**
 * Who leads the page: the operator's wordmark alone, with the name as its alt
 * text; else the operator's mark with the name beside it; else, with no logo
 * at all, Parkeze's mark with the name. Parkeze's own record has no logos, so
 * an unbranded page shows Parkeze's mark and "Parkeze".
 */
export function BrandIdentity({ brand }: { brand: BrandRecord }) {
  const { wordmark, wordmark_dark, mark, mark_dark } = brand.logo

  if (wordmark) {
    return (
      <LogoImage
        light={wordmark}
        dark={wordmark_dark}
        alt={brand.name}
        className="h-8 w-auto max-w-[min(60vw,240px)] object-contain object-left"
      />
    )
  }

  return (
    <span className="flex items-center gap-2.5 min-w-0">
      {mark ? (
        <LogoImage light={mark} dark={mark_dark} alt="" className="h-8 w-8 shrink-0 object-contain" />
      ) : (
        <img src={parkezeMark} alt="" className="h-8 w-8 shrink-0" />
      )}
      {/* The short name on a phone, where the header is tight; the full one
          from sm up. */}
      <span className="text-base font-semibold truncate sm:hidden">{brand.short_name}</span>
      <span className="text-base font-semibold truncate hidden sm:inline">{brand.name}</span>
    </span>
  )
}
